import type { BadgeColor } from "../../components/ui/Badge";
import type { Cassaforte, ModuloPromemoria, ModuloRecord, Passo, PersonaResponsabile, Responsabile, RigaPratica, StatoBase, TipoBozza, VoceDaFare } from "../../api/statoLavori";

/**
 * Come si mostrano i dati di «Stato lavori». Solo presentazione: lo stato dei
 * passi, le voci mancanti, la scadenza del preavviso li decide il backend, e
 * qui non si ricalcolano. Funzioni pure, provate da
 * `tests/stato-lavori-presentazione.test.mjs`.
 */

/** Il colore del badge per lo stato di base (decisione Q16: «In corso» in giallo). */
export const TONO_STATO: Record<StatoBase, BadgeColor> = {
  da_fare: "neutral",
  in_corso: "progress",
  completato: "success",
  non_necessario: "neutral",
  bloccato: "danger",
};

/**
 * Una data senza ora (`AAAA-MM-GG`) come «gg/mm/aaaa», letta dai pezzi della
 * stringa: con `new Date()` il browser la sposterebbe nel suo fuso.
 */
export function dataIt(chiave: string | null | undefined): string {
  if (!chiave) return "—";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(chiave);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : chiave;
}

/** Un istante (ISO con ora) come giorno italiano. */
export function istanteIt(iso: string | null | undefined): string {
  return iso ? new Date(iso).toLocaleDateString("it-IT") : "—";
}

/** Il giorno di oggi nel fuso del browser, come `AAAA-MM-GG`: il valore iniziale dei campi data. */
export function oggiChiave(adesso: Date = new Date()): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${adesso.getFullYear()}-${p(adesso.getMonth() + 1)}-${p(adesso.getDate())}`;
}

/** Come si chiama la cassaforte: la matricola, altrimenti l'etichetta data all'avvio. */
export function nomeCassaforte(c: Pick<Cassaforte, "matricola" | "etichetta">): string {
  if (c.matricola) return c.matricola;
  if (c.etichetta) return `${c.etichetta} (senza matricola)`;
  return "Senza matricola";
}

/** «Torino Centro · Torino», o quello che c'è. */
export function nomeSede(s: RigaPratica["sede"]): string {
  return [s.nome ?? "Sede senza nome", s.citta].filter(Boolean).join(" · ");
}

/** L'avanzamento in percentuale intera, per la barra. Nessun passo necessario = 100. */
export function percentuale(a: RigaPratica["avanzamento"]): number {
  return a.necessari > 0 ? Math.round((100 * a.completati) / a.necessari) : 100;
}

/**
 * L'etichetta del badge di un passo. «In attesa» quando è da fare perché manca
 * un passo prima (come il passo 9 nella bozza), «automatico» quando il «non
 * necessario» non l'ha segnato nessuno.
 */
export function etichettaStato(p: Passo, etichette: Record<StatoBase, string>): string {
  if (p.stato === "da_fare" && p.in_attesa_di) return "In attesa";
  if (p.stato === "non_necessario" && p.non_necessario?.automatico) return `${etichette.non_necessario} (automatico)`;
  if (p.stato === "bloccato" && p.stato_intermedio) return p.stato_intermedio.etichetta;
  return etichette[p.stato];
}

/** I passi raggruppati come nella scheda, nell'ordine in cui arrivano (1–5, A, 6–9). */
export function gruppiDiPassi(passi: Passo[]): { titolo: string; passi: Passo[] }[] {
  const gruppi: { titolo: string; passi: Passo[] }[] = [];
  for (const p of passi) {
    const ultimo = gruppi[gruppi.length - 1];
    if (ultimo && ultimo.titolo === p.gruppo) ultimo.passi.push(p);
    else gruppi.push({ titolo: p.gruppo, passi: [p] });
  }
  return gruppi;
}

/** Le etichette delle voci mancanti del passo 8, nell'ordine del catalogo. */
export function etichetteMancanti(p: Passo): string[] {
  const mancanti = new Set(p.vigilanza?.voci_mancanti ?? []);
  return p.voci.filter((v) => mancanti.has(v.codice)).map((v) => v.etichetta);
}

/**
 * Il prossimo stato manuale del passo, quello di «Avanza a…». NULL se il passo
 * non si avanza a mano (stati ricavati dal record, nessuno stato, già alla fine).
 */
export function prossimoStatoManuale(p: Passo): { codice: string; etichetta: string; ultimo: boolean } | null {
  if (p.stati.length === 0 || p.stati.some((s) => s.fonte === "ricavato")) return null;
  if (PASSI_CON_RECORD.includes(p.tipo)) return null;
  let indice = -1;
  p.stati.forEach((s, i) => {
    if (s.raggiunto) indice = i;
  });
  const prossimo = p.stati[indice + 1];
  return prossimo ? { codice: prossimo.codice, etichetta: prossimo.etichetta, ultimo: indice + 1 === p.stati.length - 1 } : null;
}

/** I passi con un record dietro: stati ricavati, collegamento a mano, «Apri». */
export const PASSI_CON_RECORD: readonly Passo["tipo"][] = ["sopralluogo", "installazione", "attivazione"];

/** Dove si apre il record di un passo: la pagina del modulo, col suo pannello (`?open=<id>`). */
export const ROTTA_RECORD: Record<ModuloRecord, string> = {
  sopralluogo: "/sopralluoghi",
  installazione: "/schedule",
  attivazione: "/activations",
};

/** Dove si apre l'attività di un promemoria ordini: come `ROTTA_RECORD`, più gli interventi. */
export const ROTTA_ATTIVITA: Record<ModuloPromemoria, string> = {
  ...ROTTA_RECORD,
  intervento: "/interventions",
};

/** «Apri il sopralluogo», «Apri l'installazione», «Apri l'attivazione». */
export const APRI_RECORD: Record<ModuloRecord, string> = {
  sopralluogo: "Apri il sopralluogo",
  installazione: "Apri l'installazione",
  attivazione: "Apri l'attivazione",
};

/** Il responsabile di un passo si sceglie a mano su tutti i passi tranne 6, 7 e 9: lì è quello del record. */
export function responsabileAMano(p: Passo): boolean {
  return !PASSI_CON_RECORD.includes(p.tipo);
}

/**
 * Chi preselezionare nella scelta del responsabile. La scheda dice il
 * responsabile per nome, non per id: si preseleziona solo se il nome è di una
 * persona sola dell'elenco. `null` = nessun responsabile; `undefined` = non si
 * sa (omonimi, o persona non più fra admin e staff attivi): la scelta resta da fare.
 */
export function responsabilePreselezionato(attuale: Responsabile | null, persone: PersonaResponsabile[]): string | null | undefined {
  if (attuale === null) return null;
  const stessoNome = persone.filter((u) => u.nome === attuale.nome);
  return stessoNome.length === 1 ? stessoNome[0].id : undefined;
}

/**
 * Il dispositivo che si può associare a una cassaforte di lavoro: di tipo
 * Cassaforte, oppure non classificato (decisione del 23/10/2026). È una copia
 * dichiarata della regola di `motivoDeviceNonAssociabile` (backend,
 * `services/statoLavori/scritture.ts`), che resta quella che decide: se cambia
 * lì, va cambiata qui. La usano «Avvia pratica» e «Associa dispositivo».
 */
export function dispositivoAssociabile(tipo: string | null): boolean {
  return tipo === null || tipo === "Cassaforte";
}

/** In che gruppo sta una pratica: lo stesso criterio dei filtri dell'elenco. */
export function gruppoPratica(r: RigaPratica): "in_corso" | "completata" | "chiusa" {
  if (r.chiusa) return "chiusa";
  return r.completata ? "completata" : "in_corso";
}

/**
 * Le pratiche di una sede nel riquadro della Vista Sede: prima quelle in
 * corso, poi le completate, poi le chiuse; dentro ogni gruppo la prima avviata
 * per prima. Non cambia l'array ricevuto.
 */
export function ordinaPraticheSede(pratiche: RigaPratica[]): RigaPratica[] {
  const peso = { in_corso: 0, completata: 1, chiusa: 2 } as const;
  return [...pratiche].sort(
    (a, b) => peso[gruppoPratica(a)] - peso[gruppoPratica(b)] || (a.avviata_il < b.avviata_il ? -1 : a.avviata_il > b.avviata_il ? 1 : 0)
  );
}

/** Da dove viene la data di attivazione di una sede senza pratica (decisione Q15), detto accanto alla data. */
export const FONTE_ATTIVAZIONE: Record<"attivazione" | "dispositivo", string> = {
  attivazione: "Dall'ultima attivazione effettuata con questa sede.",
  dispositivo: "Dalla data di attivazione dei dispositivi presso questa sede.",
};

// --- Bozze (PR C2) -----------------------------------------------------------

/**
 * Il link di «Conferma»: il form del modulo, che si precompila dalla bozza e,
 * dopo il salvataggio, la conferma col record creato e torna alla pratica.
 * Solo gli id: i dati li legge il form dalla bozza, così ce n'è una copia sola.
 */
export function linkConfermaBozza(bozza: { id: string; tipo: TipoBozza }, praticaId: string): string {
  const qui = new URLSearchParams({ new: "1", bozza: bozza.id, pratica: praticaId });
  return `${ROTTA_RECORD[bozza.tipo]}?${qui.toString()}`;
}

/** Dal link di «Conferma»: la bozza e la pratica a cui tornare. NULL se il form non arriva da una bozza. */
export function bozzaDalLink(search: string): { bozzaId: string; praticaId: string | null } | null {
  const qui = new URLSearchParams(search);
  const bozzaId = qui.get("bozza");
  return bozzaId ? { bozzaId, praticaId: qui.get("pratica") } : null;
}

/** «Bozza dell'installazione», «Bozza dell'attivazione». */
export const TITOLO_BOZZA: Record<TipoBozza, string> = {
  installazione: "Bozza dell'installazione",
  attivazione: "Bozza dell'attivazione",
};

/** Da dove nasce una bozza, detto nella scheda accanto alla bozza. */
export const ORIGINE_BOZZA: Record<TipoBozza, string> = {
  installazione: "Nasce dal sopralluogo approvato della cassaforte.",
  attivazione: "Nasce dall'installazione completata con «Da attivare il servizio Pre Credit».",
};

// --- Passi da fare (PR D) ------------------------------------------------------

/** Dove porta una voce del «Da fare»: il passo nella scheda della pratica, o il riquadro del servizio. */
export function linkPassoDaFare(v: Pick<VoceDaFare, "pratica_id" | "passo">): string {
  return `/stato-lavori/${v.pratica_id}#${v.passo ? `passo-${v.passo.tipo}` : "servizio"}`;
}
